import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Mail,
  Calendar,
  Phone,
  Plus,
  Search,
  Filter,
  Inbox,
  Send,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Settings,
} from 'lucide-react';
import {
  communicationHubService,
  UnifiedMessage,
  EmailAccount,
  CalendarAccount,
} from '@/services/communication/communicationHubService';
import { formatDate, formatDateTime } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface CommunicationHubProps {
  contactId?: string;
  organizationId?: string;
  opportunityId?: string;
}

export function CommunicationHub({
  contactId,
  organizationId,
  opportunityId,
}: CommunicationHubProps) {
  const [messages, setMessages] = useState<UnifiedMessage[]>([]);
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [calendarAccounts, setCalendarAccounts] = useState<CalendarAccount[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'email' | 'calendar'>('all');
  const [syncing, setSyncing] = useState<string | null>(null);
  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadData();
  }, [contactId, organizationId, opportunityId]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [messagesData, emailAccountsData, calendarAccountsData, analyticsData] =
        await Promise.all([
          communicationHubService.getUnifiedTimeline({
            contact_id: contactId,
            organization_id: organizationId,
            opportunity_id: opportunityId,
            limit: 50,
          }),
          currentUser?.id
            ? communicationHubService.getEmailAccounts(currentUser.id)
            : Promise.resolve([]),
          currentUser?.id
            ? communicationHubService.getCalendarAccounts(currentUser.id)
            : Promise.resolve([]),
          communicationHubService.getCommunicationAnalytics('week'),
        ]);

      setMessages(messagesData);
      setEmailAccounts(emailAccountsData);
      setCalendarAccounts(calendarAccountsData);
      setAnalytics(analyticsData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load communication data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const syncAccount = async (accountId: string, type: 'email' | 'calendar') => {
    try {
      setSyncing(accountId);

      if (type === 'email') {
        await communicationHubService.syncEmails(accountId);
      } else {
        await communicationHubService.syncCalendar(accountId);
      }

      await loadData(); // Refresh data

      toast({
        title: 'Success',
        description: `${type === 'email' ? 'Email' : 'Calendar'} sync completed`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to sync ${type}`,
        variant: 'destructive',
      });
    } finally {
      setSyncing(null);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'calendar':
        return <Calendar className="w-4 h-4" />;
      case 'call':
        return <Phone className="w-4 h-4" />;
      case 'meeting':
        return <Users className="w-4 h-4" />;
      default:
        return <Mail className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'text-green-600';
      case 'syncing':
        return 'text-blue-600';
      case 'error':
        return 'text-red-600';
      case 'disconnected':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const filteredMessages = messages.filter((message) => {
    const matchesSearch =
      !searchTerm ||
      message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.content?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || message.type === filterType;

    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading communications...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Communication Hub</h2>
          <p className="text-muted-foreground">Unified view of all communications and activities</p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Compose
        </Button>
      </div>

      <Tabs defaultValue="timeline" className="w-full">
        <TabsList>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search communications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="calendar">Calendar</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-1" />
              Filter
            </Button>
          </div>

          {/* Message Timeline */}
          <div className="space-y-3">
            {filteredMessages.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No communications found</h3>
                  <p className="text-muted-foreground">
                    {searchTerm
                      ? 'Try adjusting your search or filters'
                      : 'No communications available'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredMessages.map((message) => (
                <Card key={message.id} className="transition-all hover:shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          {getTypeIcon(message.type)}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{message.type}</Badge>
                            <span className="text-sm font-medium">{message.from}</span>
                            {message.to.length > 0 && (
                              <span className="text-sm text-muted-foreground">
                                → {message.to.join(', ')}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatDateTime(message.timestamp)}
                          </span>
                        </div>

                        <h4 className="font-medium mb-1 truncate">{message.subject}</h4>

                        {message.content && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {message.content}
                          </p>
                        )}

                        {message.type === 'calendar' && message.metadata.location && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {message.metadata.location}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="accounts" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Email Accounts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Email Accounts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {emailAccounts.length === 0 ? (
                  <div className="text-center py-6">
                    <Mail className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No email accounts connected</p>
                    <Button variant="outline" size="sm" className="mt-2">
                      <Plus className="w-4 h-4 mr-1" />
                      Connect Account
                    </Button>
                  </div>
                ) : (
                  emailAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Mail className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{account.email_address}</p>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className={getStatusColor(account.sync_status)}
                            >
                              {account.sync_status}
                            </Badge>
                            {account.is_primary && <Badge variant="secondary">Primary</Badge>}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => syncAccount(account.id, 'email')}
                          disabled={syncing === account.id}
                        >
                          <RefreshCw
                            className={`w-4 h-4 ${syncing === account.id ? 'animate-spin' : ''}`}
                          />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Calendar Accounts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Calendar Accounts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {calendarAccounts.length === 0 ? (
                  <div className="text-center py-6">
                    <Calendar className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No calendar accounts connected</p>
                    <Button variant="outline" size="sm" className="mt-2">
                      <Plus className="w-4 h-4 mr-1" />
                      Connect Calendar
                    </Button>
                  </div>
                ) : (
                  calendarAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{account.calendar_name}</p>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className={getStatusColor(account.sync_status)}
                            >
                              {account.sync_status}
                            </Badge>
                            <Badge variant="outline">{account.provider}</Badge>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => syncAccount(account.id, 'calendar')}
                          disabled={syncing === account.id}
                        >
                          <RefreshCw
                            className={`w-4 h-4 ${syncing === account.id ? 'animate-spin' : ''}`}
                          />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          {analytics && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Emails</p>
                      <p className="text-2xl font-bold">{analytics.email_stats.total_emails}</p>
                    </div>
                    <Mail className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Sent Emails</p>
                      <p className="text-2xl font-bold">{analytics.email_stats.sent_emails}</p>
                    </div>
                    <Send className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Response Rate</p>
                      <p className="text-2xl font-bold">{analytics.email_stats.response_rate}%</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Meetings</p>
                      <p className="text-2xl font-bold">{analytics.calendar_stats.total_events}</p>
                    </div>
                    <Calendar className="w-8 h-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
